import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Share2, 
  Download,
  Printer,
  Building2,
  DollarSign,
  TrendingUp,
  Calendar,
  MapPin,
  CheckCircle2,
  Clock,
  X,
  Loader2
} from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

const ReportPreviewModal = ({ open, onClose, reportData, onDownload, onShare, downloading, sharing }) => {
  if (!reportData) return null;

  const { project, financials, progress, expense_breakdown, comps, comp_stats, generated_at } = reportData;

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto print:max-w-none print:max-h-none print:overflow-visible">
        <DialogHeader className="print:hidden">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Report Preview
          </DialogTitle>
          <DialogDescription>
            Preview the investment report before downloading
          </DialogDescription>
        </DialogHeader>

        {/* Report Content */}
        <div className="space-y-6 py-4" id="report-preview-content">
          {/* Header */}
          <div className="text-center border-b border-border pb-6">
            <h1 className="text-2xl font-bold text-primary mb-2">DealVault Investment Report</h1>
            <p className="text-sm text-muted-foreground">Generated: {formatDate(generated_at)}</p>
          </div>

          {/* Property Details */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2 text-blue-400">
              <Building2 className="w-4 h-4" />
              Property Details
            </h2>
            <div className="grid grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-sm">
              <div>
                <p className="text-xs text-muted-foreground uppercase">Property Name</p>
                <p className="font-medium">{project.name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase">Status</p>
                <Badge variant="outline" className="rounded-sm mt-1">
                  {project.status}
                </Badge>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground uppercase">Address</p>
                <p className="font-medium flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {project.address}, {project.city}, {project.state} {project.zipcode}
                </p>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2 text-amber-400">
              <DollarSign className="w-4 h-4" />
              Financial Summary
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-secondary/30 rounded-sm">
                <p className="text-xs text-muted-foreground uppercase">Purchase Price</p>
                <p className="text-lg font-bold font-mono">{formatCurrency(financials.purchase_price)}</p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-sm">
                <p className="text-xs text-muted-foreground uppercase">Amount Spent</p>
                <p className="text-lg font-bold font-mono text-amber-400">{formatCurrency(financials.amount_spent)}</p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-sm">
                <p className="text-xs text-muted-foreground uppercase">Estimated ARV</p>
                <p className="text-lg font-bold font-mono text-green-400">{formatCurrency(financials.estimated_arv)}</p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-sm">
                <p className="text-xs text-muted-foreground uppercase">Potential Profit</p>
                <p className={`text-lg font-bold font-mono ${financials.potential_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(financials.potential_profit)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-secondary/30 rounded-sm text-center">
                <p className="text-xs text-muted-foreground uppercase">Target Budget</p>
                <p className="font-mono font-medium">{formatCurrency(financials.target_budget)}</p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-sm text-center">
                <p className="text-xs text-muted-foreground uppercase">Budget Remaining</p>
                <p className={`font-mono font-medium ${financials.budget_remaining >= 0 ? '' : 'text-red-400'}`}>
                  {formatCurrency(financials.budget_remaining)}
                </p>
              </div>
              <div className="p-3 bg-secondary/30 rounded-sm text-center">
                <p className="text-xs text-muted-foreground uppercase">ROI</p>
                <p className={`font-mono font-medium ${financials.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {financials.roi}%
                </p>
              </div>
            </div>
          </div>

          {/* Project Progress */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2 text-blue-400">
              <CheckCircle2 className="w-4 h-4" />
              Project Progress
            </h2>
            <div className="grid grid-cols-2 gap-4 p-4 bg-secondary/30 rounded-sm">
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-2">Tasks Completed</p>
                <p className="text-xl font-bold font-mono">{progress.completed_tasks}/{progress.total_tasks}</p>
                <Progress value={progress.completion_percentage} className="h-2 mt-2" />
                <p className="text-xs text-muted-foreground mt-1">{progress.completion_percentage}% complete</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase mb-2">Budget Used</p>
                <p className="text-xl font-bold font-mono">{progress.budget_used_percentage}%</p>
                <Progress value={Math.min(progress.budget_used_percentage, 100)} className="h-2 mt-2" />
                <p className="text-xs text-muted-foreground mt-1">of target budget</p>
              </div>
            </div>
          </div>

          {/* Expense Breakdown */}
          {expense_breakdown && expense_breakdown.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold flex items-center gap-2 text-amber-400">
                <DollarSign className="w-4 h-4" />
                Expense Breakdown
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-2 text-xs text-muted-foreground uppercase">Category</th>
                      <th className="text-right p-2 text-xs text-muted-foreground uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expense_breakdown.map((expense, idx) => (
                      <tr key={idx} className="border-b border-border/50">
                        <td className="p-2">{expense.category}</td>
                        <td className="p-2 text-right font-mono">{formatCurrency(expense.amount)}</td>
                      </tr>
                    ))}
                    <tr className="bg-secondary/30 font-bold">
                      <td className="p-2">TOTAL</td>
                      <td className="p-2 text-right font-mono">{formatCurrency(financials.amount_spent)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Comparable Sales */}
          {comps && comps.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold flex items-center gap-2 text-green-400">
                <TrendingUp className="w-4 h-4" />
                Comparable Sales Analysis
              </h2>
              {comp_stats && (
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="p-2 bg-secondary/30 rounded-sm text-center">
                    <p className="text-xs text-muted-foreground">Count</p>
                    <p className="font-bold">{comp_stats.count}</p>
                  </div>
                  <div className="p-2 bg-secondary/30 rounded-sm text-center">
                    <p className="text-xs text-muted-foreground">Average</p>
                    <p className="font-mono text-sm">{formatCurrency(comp_stats.average)}</p>
                  </div>
                  <div className="p-2 bg-secondary/30 rounded-sm text-center">
                    <p className="text-xs text-muted-foreground">Min</p>
                    <p className="font-mono text-sm">{formatCurrency(comp_stats.min)}</p>
                  </div>
                  <div className="p-2 bg-secondary/30 rounded-sm text-center">
                    <p className="text-xs text-muted-foreground">Max</p>
                    <p className="font-mono text-sm">{formatCurrency(comp_stats.max)}</p>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-blue-500/20">
                      <th className="text-left p-2 text-xs uppercase">Address</th>
                      <th className="text-right p-2 text-xs uppercase">Price</th>
                      <th className="text-center p-2 text-xs uppercase">Beds/Baths</th>
                      <th className="text-right p-2 text-xs uppercase">Sqft</th>
                      <th className="text-right p-2 text-xs uppercase">Sale Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comps.slice(0, 10).map((comp, idx) => (
                      <tr key={idx} className="border-b border-border/50">
                        <td className="p-2 truncate max-w-[200px]">{comp.address}</td>
                        <td className="p-2 text-right font-mono">{formatCurrency(comp.sale_price)}</td>
                        <td className="p-2 text-center">{comp.beds}/{comp.baths}</td>
                        <td className="p-2 text-right">{comp.sqft?.toLocaleString()}</td>
                        <td className="p-2 text-right">{formatDate(comp.sale_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground">
              This report is for informational purposes only. Verify all data before making investment decisions.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-border print:hidden">
          <Button variant="outline" onClick={handlePrint} className="rounded-sm">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button 
            onClick={onDownload} 
            disabled={downloading}
            variant="outline"
            className="rounded-sm"
          >
            {downloading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Downloading...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Download
              </>
            )}
          </Button>
          <Button 
            onClick={onShare} 
            disabled={sharing}
            className="bg-primary text-primary-foreground rounded-sm"
          >
            {sharing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating Link...
              </>
            ) : (
              <>
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportPreviewModal;
